import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, Section, EmptyState } from "../../components/Common";
import { toast } from "sonner";
import { Plus, BookOpen, Upload, Loader2, Pencil, Trash2, X, ClipboardCheck } from "lucide-react";
import { useDataSync } from "../../lib/realtime";
import { HomeworkSubmissionsPanel } from "./HomeworkSubmissionsPanel";

export default function TeacherHomework() {
  const [list, setList] = useState([]);
  const [batches, setBatches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [expandedHw, setExpandedHw] = useState(null);
  const [form, setForm] = useState({ batch_id: "", title: "", description: "", due_date: "", file_url: "", video_url: "" });

  const load = async () => {
    const [h, b] = await Promise.all([api.get("/homework"), api.get("/teacher/batches")]);
    setList(h.data);
    setBatches(b.data);
  };
  useEffect(() => { load(); }, []);
  useDataSync("homework", () => load());

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.due_date) delete payload.due_date;
      if (editing) {
        await api.patch(`/teacher/homework/${editing.id}`, payload);
        toast.success("Homework updated");
      } else {
        await api.post("/teacher/homework", payload);
        toast.success("Homework added");
      }
      setForm({ batch_id: "", title: "", description: "", due_date: "", file_url: "", video_url: "" });
      setShowForm(false); setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startEdit = (hw) => {
    setEditing(hw);
    setForm({
      batch_id: hw.batch_id, title: hw.title, description: hw.description || "",
      due_date: hw.due_date || "", file_url: hw.file_url || "", video_url: hw.video_url || "",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (hw) => {
    if (!confirm(`Delete "${hw.title}"?`)) return;
    try {
      await api.delete(`/teacher/homework/${hw.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, file_url: fullUrl }));
      toast.success(`Uploaded ${data.filename}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <PageHeader
        title="Homework"
        subtitle="Share notes, worksheets, and video links with your students."
        actions={
          <button onClick={() => { setShowForm((v) => !v); setEditing(null); setForm({ batch_id: "", title: "", description: "", due_date: "", file_url: "", video_url: "" }); }}
            data-testid="btn-add-homework"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
            <Plus className="h-4 w-4" /> Add homework
          </button>
        }
      />

      {showForm && (
        <Section title={editing ? `Edit: ${editing.title}` : "New assignment"} actions={
          <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        }>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="hw-form">
            <div>
              <label className="text-sm font-semibold text-slate-700">Batch</label>
              <select required value={form.batch_id} onChange={(e) => setForm({ ...form, batch_id: e.target.value })}
                data-testid="hw-batch" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none">
                <option value="">— select —</option>
                {batches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Due date</label>
              <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="hw-due" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-slate-700">Title</label>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="hw-title" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-slate-700">Description</label>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="hw-desc" className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">PDF / file</label>
              <div className="mt-1.5 flex gap-2">
                <input value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })}
                  data-testid="hw-file" placeholder="https://... or upload" className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
                <label className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg bg-blue-50 text-blue-700 font-semibold text-sm cursor-pointer hover:bg-blue-100">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? "..." : "Upload"}
                  <input type="file" hidden onChange={onFile} data-testid="hw-file-input"
                    accept=".pdf,image/*,.doc,.docx,.txt" />
                </label>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Video URL</label>
              <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                data-testid="hw-video" placeholder="https://youtu.be/..." className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div className="sm:col-span-2 flex gap-2">
              <button type="submit" data-testid="hw-submit" className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
                {editing ? "Save changes" : "Add"}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </Section>
      )}

      <div className="mt-6 space-y-3">
        {list.map((h) => {
          const batch = batches.find((b) => b.id === h.batch_id);
          return (
            <div key={h.id} className="bg-white border border-slate-200 rounded-xl p-4 ym-card-hover">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 grid place-items-center shrink-0">
                    <BookOpen className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-slate-900">{h.title}</h3>
                    {h.description && <p className="text-sm text-slate-600 mt-0.5">{h.description}</p>}
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                      {batch && <span>Batch: {batch.name}</span>}
                      {h.due_date && <span>Due {h.due_date}</span>}
                      {h.file_url && <a href={h.file_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">📄 File</a>}
                      {h.video_url && <a href={h.video_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">🎥 Video</a>}
                      {h.version > 1 && <span className="text-slate-400">v{h.version}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setExpandedHw(expandedHw === h.id ? null : h.id)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${expandedHw === h.id ? "bg-blue-600 text-white" : "text-blue-600 bg-blue-50 hover:bg-blue-100"}`}
                    data-testid={`hw-submissions-btn-${h.id}`}>
                    <ClipboardCheck className="h-4 w-4" /> Submissions
                  </button>
                  <button onClick={() => startEdit(h)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg" data-testid={`edit-hw-${h.id}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(h)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded-lg" data-testid={`delete-hw-${h.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {expandedHw === h.id && <HomeworkSubmissionsPanel hwId={h.id} />}
            </div>
          );
        })}
        {list.length === 0 && <EmptyState title="No homework yet" desc="Create your first assignment." icon={BookOpen} />}
      </div>
    </div>
  );
}
